import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export interface Tribunal {
  id: string;
  nome: string;
  sigla: string;
  tipo: string;
  created_at: string;
}

export interface Jurisprudencia {
  id: string;
  tribunal_id: string;
  numero_processo: string;
  ementa: string;
  relator: string | null;
  data_julgamento: string | null;
  orgao_julgador: string | null;
  area_direito: string | null;
  palavras_chave: string[];
  texto_completo: string | null;
  metadata: Record<string, any>;
  importado_por: string | null;
  created_at: string;
  updated_at: string;
  tribunais?: Tribunal;
}

interface Stats {
  totalJurisprudencias: number;
  totalTribunais: number;
  totalViews: number;
  totalSaves: number;
  totalSearches: number;
  topSearchTerms: { term: string; count: number }[];
  mostViewed: { id: string; ementa: string; views: number }[];
  mostSaved: { id: string; ementa: string; saves: number }[];
  byTribunal: { sigla: string; count: number }[];
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export const useJurisprudencias = () => {
  const { user } = useAuth();
  const [tribunais, setTribunais] = useState<Tribunal[]>([]);
  const [jurisprudencias, setJurisprudencias] = useState<Jurisprudencia[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const isMasterAdmin = user?.email === 'wagner.jeveaux@gmail.com';

  const fetchTribunais = useCallback(async () => {
    const { data, error } = await supabase
      .from('tribunais')
      .select('*')
      .order('sigla');
    if (!error && data) setTribunais(data);
  }, []);

  const fetchJurisprudencias = useCallback(async () => {
    const { data, error } = await supabase
      .from('jurisprudencias')
      .select('*, tribunais(nome, sigla)')
      .order('created_at', { ascending: false })
      .limit(100);
    if (!error && data) setJurisprudencias(data as any);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const [
        { count: totalJuris },
        { count: totalTrib },
        { count: totalViews },
        { count: totalSaves },
        { count: totalSearches },
      ] = await Promise.all([
        supabase.from('jurisprudencias').select('*', { count: 'exact', head: true }),
        supabase.from('tribunais').select('*', { count: 'exact', head: true }),
        supabase.from('jurisprudencia_views').select('*', { count: 'exact', head: true }),
        supabase.from('jurisprudencia_saves').select('*', { count: 'exact', head: true }),
        supabase.from('jurisprudencia_searches').select('*', { count: 'exact', head: true }),
      ]);

      // Top search terms
      const { data: searchData } = await supabase
        .from('jurisprudencia_searches')
        .select('search_term');
      
      const termCounts: Record<string, number> = {};
      searchData?.forEach(s => {
        const t = s.search_term.toLowerCase().trim();
        termCounts[t] = (termCounts[t] || 0) + 1;
      });
      const topSearchTerms = Object.entries(termCounts)
        .map(([term, count]) => ({ term, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Most viewed
      const { data: viewData } = await supabase
        .from('jurisprudencia_views')
        .select('jurisprudencia_id');
      
      const viewCounts: Record<string, number> = {};
      viewData?.forEach(v => {
        viewCounts[v.jurisprudencia_id] = (viewCounts[v.jurisprudencia_id] || 0) + 1;
      });
      const topViewedIds = Object.entries(viewCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      let mostViewed: Stats['mostViewed'] = [];
      if (topViewedIds.length > 0) {
        const { data: jurisViewed } = await supabase
          .from('jurisprudencias')
          .select('id, ementa')
          .in('id', topViewedIds.map(([id]) => id));
        mostViewed = topViewedIds.map(([id, views]) => ({
          id,
          ementa: jurisViewed?.find(j => j.id === id)?.ementa?.substring(0, 100) || '',
          views,
        }));
      }

      // Most saved
      const { data: saveData } = await supabase
        .from('jurisprudencia_saves')
        .select('jurisprudencia_id');
      
      const saveCounts: Record<string, number> = {};
      saveData?.forEach(s => {
        saveCounts[s.jurisprudencia_id] = (saveCounts[s.jurisprudencia_id] || 0) + 1;
      });
      const topSavedIds = Object.entries(saveCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      let mostSaved: Stats['mostSaved'] = [];
      if (topSavedIds.length > 0) {
        const { data: jurisSaved } = await supabase
          .from('jurisprudencias')
          .select('id, ementa')
          .in('id', topSavedIds.map(([id]) => id));
        mostSaved = topSavedIds.map(([id, saves]) => ({
          id,
          ementa: jurisSaved?.find(j => j.id === id)?.ementa?.substring(0, 100) || '',
          saves,
        }));
      }

      // By tribunal
      const { data: tribunalData } = await supabase
        .from('jurisprudencias')
        .select('tribunal_id, tribunais(sigla)');
      
      const tribunalCounts: Record<string, number> = {};
      tribunalData?.forEach((j: any) => {
        const sigla = j.tribunais?.sigla || 'Outro';
        tribunalCounts[sigla] = (tribunalCounts[sigla] || 0) + 1;
      });
      const byTribunal = Object.entries(tribunalCounts)
        .map(([sigla, count]) => ({ sigla, count }))
        .sort((a, b) => b.count - a.count);

      setStats({
        totalJurisprudencias: totalJuris || 0,
        totalTribunais: totalTrib || 0,
        totalViews: totalViews || 0,
        totalSaves: totalSaves || 0,
        totalSearches: totalSearches || 0,
        topSearchTerms,
        mostViewed,
        mostSaved,
        byTribunal,
      });
    } catch (err) {
      console.error('Erro ao buscar estatísticas:', err);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchTribunais(), fetchJurisprudencias(), fetchStats()]);
    setIsLoading(false);
  }, [fetchTribunais, fetchJurisprudencias, fetchStats]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const addTribunal = async (nome: string, sigla: string, tipo: string) => {
    const { error } = await supabase.from('tribunais').insert({ nome, sigla, tipo });
    if (error) {
      toast.error('Erro ao adicionar tribunal: ' + error.message);
      return false;
    }
    toast.success('Tribunal adicionado!');
    await fetchTribunais();
    return true;
  };

  const deleteTribunal = async (id: string) => {
    const { error } = await supabase.from('tribunais').delete().eq('id', id);
    if (error) {
      toast.error('Erro ao excluir tribunal: ' + error.message);
      return;
    }
    toast.success('Tribunal excluído!');
    await Promise.all([fetchTribunais(), fetchJurisprudencias()]);
  };

  // Helper to extract data from texto_completo
  const extractFromTexto = (texto: string) => {
    // Extract numero_processo - pattern like 08006059020258230010 or 0800605-90.2025.8.23.0010
    const processoMatch = texto.match(/(\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4})/);
    const processoRawMatch = texto.match(/(?:PROCESSO|Jurisprudência\/\w+\s*-\s*)(\d{20})/);
    const numero_processo = processoMatch?.[1] || processoRawMatch?.[1] || '';

    // Extract ementa - text after "EMENTA\n" or "Ementa:"
    let ementa = '';
    const ementaIdx = texto.indexOf('EMENTA\n');
    if (ementaIdx !== -1) {
      const afterEmenta = texto.substring(ementaIdx + 7);
      // Take until ACÓRDÃO or end
      const endIdx = afterEmenta.indexOf('ACÓRDÃO');
      ementa = (endIdx !== -1 ? afterEmenta.substring(0, endIdx) : afterEmenta).trim();
      // Clean up: remove "Ementa:" prefix if present
      ementa = ementa.replace(/^Ementa:\s*/i, '').trim();
    }

    // Extract relator
    const relatorMatch = texto.match(/Relator\(a\):\s*(.+?)(?:\n|$)/);
    const relator = relatorMatch?.[1]?.trim() || null;

    // Extract orgao_julgador
    const orgaoMatch = texto.match(/ÓRGÃO JULGADOR\s*\n(.+?)(?:\n|$)/);
    const orgao_julgador = orgaoMatch?.[1]?.trim() || null;

    // Extract data_julgamento
    const dataMatch = texto.match(/DATA DO JULGAMENTO\s*\n?DATA PUBLICAÇÃO\s*\n(\d{2}\/\d{2}\/\d{4})/);
    let data_julgamento: string | null = null;
    if (dataMatch) {
      const [d, m, y] = dataMatch[1].split('/');
      data_julgamento = `${y}-${m}-${d}`;
    }

    // Detect tribunal from URL or text
    const tribunalMatch = texto.match(/Jurisprudência\/(\w+)/);
    const tribunal_sigla = tribunalMatch?.[1] || null;

    return { numero_processo, ementa, relator, orgao_julgador, data_julgamento, tribunal_sigla };
  };

  const importJsonl = async (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`Arquivo muito grande. Limite máximo: ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      return;
    }

    setIsImporting(true);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n').filter(l => l.trim());
      setImportProgress({ current: 0, total: lines.length });

      // Refresh tribunais list before import
      await fetchTribunais();

      const errors: string[] = [];
      const batchSize = 50;
      let successCount = 0;

      // Collect all unique tribunal siglas we need to auto-create
      const neededTribunais = new Set<string>();

      for (const line of lines) {
        try {
          const obj = JSON.parse(line);
          // Support both direct fields and texto_completo extraction
          if (obj.texto_completo && !obj.ementa) {
            const extracted = extractFromTexto(obj.texto_completo);
            if (extracted.tribunal_sigla) neededTribunais.add(extracted.tribunal_sigla);
          }
          if (obj.tribunal_sigla) neededTribunais.add(obj.tribunal_sigla);
          if (obj.tribunal) neededTribunais.add(obj.tribunal);
        } catch { /* skip */ }
      }

      // Auto-create missing tribunais
      const currentTribunais = [...tribunais];
      for (const sigla of neededTribunais) {
        if (!currentTribunais.find(t => t.sigla === sigla)) {
          const { data, error } = await supabase
            .from('tribunais')
            .insert({ nome: `Tribunal ${sigla}`, sigla, tipo: 'tribunal' })
            .select()
            .single();
          if (!error && data) currentTribunais.push(data);
        }
      }

      for (let i = 0; i < lines.length; i += batchSize) {
        const batch = lines.slice(i, i + batchSize);
        const records: any[] = [];

        for (let j = 0; j < batch.length; j++) {
          const lineNum = i + j + 1;
          try {
            const obj = JSON.parse(batch[j]);

            let numero_processo = obj.numero_processo || '';
            let ementa = obj.ementa || '';
            let relator = obj.relator || null;
            let orgao_julgador = obj.orgao_julgador || null;
            let data_julgamento = obj.data_julgamento || null;
            let tribunal_sigla = obj.tribunal_sigla || obj.tribunal || null;
            let texto_completo = obj.texto_completo || null;

            // If texto_completo present and fields missing, extract them
            if (texto_completo && (!ementa || !numero_processo)) {
              const extracted = extractFromTexto(texto_completo);
              if (!numero_processo) numero_processo = extracted.numero_processo;
              if (!ementa) ementa = extracted.ementa;
              if (!relator) relator = extracted.relator;
              if (!orgao_julgador) orgao_julgador = extracted.orgao_julgador;
              if (!data_julgamento) data_julgamento = extracted.data_julgamento;
              if (!tribunal_sigla) tribunal_sigla = extracted.tribunal_sigla;
            }

            if (!ementa && !texto_completo) {
              errors.push(`Linha ${lineNum}: sem ementa nem texto_completo`);
              continue;
            }

            // Use texto_completo as ementa fallback
            if (!ementa && texto_completo) {
              ementa = texto_completo.substring(0, 2000);
            }

            if (!numero_processo) {
              numero_processo = obj.id_pagina || `SEM-NUMERO-${lineNum}`;
            }

            // Find tribunal
            let tribunalId: string | null = null;
            if (obj.tribunal_id) {
              tribunalId = obj.tribunal_id;
            } else if (tribunal_sigla) {
              const found = currentTribunais.find(t => t.sigla === tribunal_sigla);
              if (found) tribunalId = found.id;
            }

            if (!tribunalId) {
              // Use first tribunal as fallback or skip
              if (currentTribunais.length > 0) {
                tribunalId = currentTribunais[0].id;
              } else {
                errors.push(`Linha ${lineNum}: nenhum tribunal disponível`);
                continue;
              }
            }

            records.push({
              tribunal_id: tribunalId,
              numero_processo,
              ementa,
              relator,
              data_julgamento,
              orgao_julgador,
              area_direito: obj.area_direito || null,
              palavras_chave: obj.palavras_chave || [],
              texto_completo,
              metadata: {
                id_pagina: obj.id_pagina,
                url_origem: obj.url_origem,
                id_arquivo_real: obj.id_arquivo_real,
                ...(obj.metadata || {}),
              },
              importado_por: user?.id,
            });
          } catch {
            errors.push(`Linha ${lineNum}: JSON inválido`);
          }
        }

        if (records.length > 0) {
          const { error, data } = await supabase.from('jurisprudencias').insert(records).select('id');
          if (error) {
            errors.push(`Batch ${Math.floor(i / batchSize) + 1}: ${error.message}`);
          } else {
            successCount += data?.length || 0;
          }
        }

        setImportProgress({ current: Math.min(i + batchSize, lines.length), total: lines.length });
      }

      if (errors.length > 0) {
        toast.warning(`Importação: ${successCount} OK, ${errors.length} erro(s)`, {
          description: errors.slice(0, 3).join('; '),
        });
      } else {
        toast.success(`${successCount} jurisprudências importadas com sucesso!`);
      }

      await Promise.all([fetchTribunais(), fetchJurisprudencias(), fetchStats()]);
    } catch (err: any) {
      toast.error('Erro na importação: ' + err.message);
    } finally {
      setIsImporting(false);
      setImportProgress({ current: 0, total: 0 });
    }
  };

  const deleteJurisprudencia = async (id: string) => {
    const { error } = await supabase.from('jurisprudencias').delete().eq('id', id);
    if (error) {
      toast.error('Erro ao excluir: ' + error.message);
      return;
    }
    toast.success('Jurisprudência excluída!');
    await Promise.all([fetchJurisprudencias(), fetchStats()]);
  };

  const updateJurisprudencia = async (id: string, data: Partial<Jurisprudencia>) => {
    const { error } = await supabase.from('jurisprudencias').update(data).eq('id', id);
    if (error) {
      toast.error('Erro ao atualizar: ' + error.message);
      return false;
    }
    toast.success('Jurisprudência atualizada!');
    await fetchJurisprudencias();
    return true;
  };

  return {
    tribunais,
    jurisprudencias,
    stats,
    isLoading,
    isImporting,
    importProgress,
    isMasterAdmin,
    addTribunal,
    deleteTribunal,
    importJsonl,
    deleteJurisprudencia,
    updateJurisprudencia,
    refresh: loadAll,
  };
};
